import { createSelector } from '@ngrx/store';
import * as equal from 'fast-deep-equal';
import {
  featureFlagFeature,
  featureFlagsAdapter,
  featureFlagsFetchingAdapter,
} from 'src/app/core/state/slices/feature-flag/feature-flag.reducer';

// Just omitting name and reducer only to keep selectors for cleanliness.
const { name, reducer, ...featureFlagFeatureSelectors } = featureFlagFeature;

// Selectors from entity adapter
const featureFlagsEntitySelectors = featureFlagsAdapter.getSelectors();

// Selectors from entity adapter
const featureFlagsFetchingEntitySelectors = featureFlagsFetchingAdapter.getSelectors();

/**
 * Checks if an existing request hash key is being processed.
 *
 * @param featureFlagKeysHash - The generated feature flag hash of a request.
 */
const selectIsBeingRequested = (featureFlagKeysHash: string) =>
  createSelector(
    featureFlagFeature.selectFeatureFlagsFetching,
    (state) => featureFlagsFetchingEntitySelectors.selectEntities(state)[featureFlagKeysHash],
  );

/**
 * Retrieves the feature flag from the store. If a feature flag has expired, it will not be included
 * in the returned object.
 *
 * @param featureFlagKeys - The list of feature flag keys to retrieve.
 * @param attributes - The custom attributes of the feature flags.
 * @returns Object containing all valid feature flag from the store.
 */
const selectFeatureFlagsByKey = <K extends string>(featureFlagKeys: K[], attributes?: any) =>
  createSelector(featureFlagFeature.selectFeatureFlags, (state): Record<K, boolean> => {
    const willCheckAttributes = attributes && typeof attributes === 'object' && Object.keys(attributes).length > 0;

    return featureFlagKeys.reduce(
      (featureFlags, flag) => {
        const flagFromStore = featureFlagsEntitySelectors.selectEntities(state)[flag];
        if (flagFromStore) {
          const isExpired = Date.now() > flagFromStore.expiry;
          const didAttributesChanged = willCheckAttributes ? !equal(attributes, flagFromStore.attributes) : false;
          if (!isExpired && !didAttributesChanged) {
            return {
              ...featureFlags,
              [flag]: flagFromStore.enabled,
            };
          }
        }
        return featureFlags;
      },
      {} as Record<K, boolean>,
    );
  });

/**
 * Checks if feature flags can be requested from the BE.
 *
 * @param featureFlagKeysHash - The hash of feature flag keys.
 * @param featureFlagKeys - The list of feature flag keys to retrieve.
 * @param attributes - The custom attributes of the feature flags.
 */
const selectCanRequestFeatureFlags = <K extends string>(
  featureFlagKeysHash: string,
  featureFlagKeys: K[],
  attributes?: any,
) =>
  createSelector(
    selectIsBeingRequested(featureFlagKeysHash),
    selectFeatureFlagsByKey(featureFlagKeys, attributes),
    (isBeingRequested, featureFlagsFromStore) =>
      !isBeingRequested && Object.keys(featureFlagsFromStore).length !== featureFlagKeys.length,
  );

export const FeatureFlagSelectors = {
  // Automatically destructure selectors available from feature reducer
  ...featureFlagFeatureSelectors,
  selectFeatureFlagsByKey,
  selectIsBeingRequested,
  selectCanRequestFeatureFlags,
};
